import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ChipMenu, ChipOptions} from '../builder/ChipMenu';
import {normalizeSearch} from '../rulesModel';

const ALL_CLIENTS = '';
// Con más clientes que éstos, el menú trae un buscador: una lista larga no se recorre a ojo.
export const CLIENT_SEARCH_FROM = 8;

/**
 * «Por cliente» de «Ahora»: el chip que deja la bandeja con las alertas de UN cliente (`?cliente=<id>`).
 * Abre la lista de los clientes que hoy tienen alertas, por nombre, con sus vigentes al lado y, arriba,
 * «Todos los clientes» para soltar el filtro.
 *
 * Es un selector, no un contador: no lleva número (el de «Todas» ya dice cuántas quedan). Sin clientes con
 * alertas queda deshabilitado, salvo que esté prendido (hay que poder apagarlo).
 *
 * `clientName` es el nombre del elegido aunque hoy no esté en la lista (la regla elegida lo dejó afuera);
 * sin él (ya no tiene alertas), «Cliente sin alertas».
 */
export function ClientFilterChip({clients, clientId, clientName, onChange}) {
    const {t} = useTranslation();
    const isOn = clientId !== null;
    let text = t('alerts_center.now.filter_client');
    if (isOn) {
        text = clientName
            ? t('alerts_center.now.filter_client_on', {name: clientName, interpolation: {escapeValue: false}})
            : t('alerts_center.now.filter_client_gone');
    }

    return (
        <ChipMenu buttonClassName={`cav-chip alc-now__client-chip${isOn ? ' -on' : ''}`}
                  slot="now-client" menuLabel={t('alerts_center.now.filter_client_menu')}
                  disabled={!isOn && clients.length === 0}
                  label={<span className="alc-now__client-name">{text}</span>}>
            {({close}) => (
                <ClientOptions clients={clients} value={isOn ? clientId : ALL_CLIENTS} close={close}
                               onChoose={(value) => onChange(value === ALL_CLIENTS ? null : value)}/>
            )}
        </ChipMenu>
    );
}

// El contenido del menú: vive mientras el menú está abierto, así que la búsqueda arranca vacía cada vez.
function ClientOptions({clients, value, onChoose, close}) {
    const {t} = useTranslation();
    const [query, setQuery] = useState('');
    const withSearch = clients.length > CLIENT_SEARCH_FROM;
    const needle = normalizeSearch(query);
    const shown = needle ? clients.filter((client) => normalizeSearch(client.name).includes(needle)) : clients;
    const hintOf = (client) => (client.active > 0
        ? t('alerts_center.now.group_active', {count: client.active})
        : t('alerts_center.now.group_snoozed', {count: client.snoozed}));
    const options = [
        {value: ALL_CLIENTS, label: t('alerts_center.now.filter_client_all')},
        ...shown.map((client) => ({value: client.id, label: client.name, hint: hintOf(client)})),
    ];
    const list = <ChipOptions options={options} value={value} close={close} onChoose={onChoose}/>;
    if (!withSearch) return list;

    return (
        <div className="alc-pick alc-now__client-pick">
            <input type="search" className="alc-pick__search" data-autofocus value={query}
                   placeholder={t('alerts_center.now.filter_client_search')}
                   aria-label={t('alerts_center.now.filter_client_search')}
                   onChange={(event) => setQuery(event.target.value)}/>
            {list}
            {needle && shown.length === 0 && (
                <p className="alc-pick__empty" data-testid="alc-now-client-none">{t('alerts_center.builder.pick_none')}</p>
            )}
        </div>
    );
}
